import { useEffect, useState } from 'react';

import {
  IconCircle,
  IconCircleCheck,
  IconLink,
  IconNote,
} from '@tabler/icons-react';
import { IconCircleCheckDisabled, IconCircleDisabled } from 'icons';
import { useTranslation } from 'react-i18next';

import { ContentStep, PlanAssignment } from '@cfa/pathway-fe-shared/xp';
import { Typography } from '@cfa/react-components';

import { getStepNote, getStepUrls } from '@/utils/contentStep';
import { cn } from '@/utils/tailwind';

import { itemByLanguage } from 'utils/language';

import PlanItemStepLink from './PlanItemStepLink';

interface PlanItemTaskProps {
  disabled?: boolean;
  isActive: boolean;
  isComplete: boolean;
  language?: string;
  onClick: (step: ContentStep) => void;
  onCompleteToggle: () => void;
  planId: string;
  statuses: PlanAssignment[];
  step: ContentStep;
  userId?: string;
}

const PlanItemTask: React.FC<PlanItemTaskProps> = ({
  disabled,
  isActive,
  isComplete,
  language,
  onClick,
  onCompleteToggle,
  planId,
  statuses,
  step,
  userId,
}) => {
  const [isCompleted, setIsCompleted] = useState(isComplete);
  const { t } = useTranslation();
  const stepUrls = getStepUrls(step);
  const noteName = itemByLanguage(getStepNote(step), undefined, language);
  const firstUrlName = itemByLanguage(
    stepUrls?.[0]?.urlSet,
    undefined,
    language,
  );
  const hasNotes = !!noteName;
  const hasUrls = !!firstUrlName;
  const canExpand = hasNotes || hasUrls;
  const userStatus = statuses
    .find(status => status.userId === userId)
    ?.steps?.find(statusStep => statusStep.stepId === step.id);
  // Use `duration` (API field) not `stepDuration` (legacy normalized field).
  // See PATHWAY-2935 for context.
  const stepDuration = userStatus?.duration
    ? Math.round(userStatus.duration / 60)
    : 0;

  useEffect(() => {
    setIsCompleted(isComplete);
  }, [isComplete]);

  const expandTask = isActive && (hasNotes || hasUrls);

  return (
    <>
      <div
        className={cn('flex w-full items-center gap-2', {
          'pb-2.5': expandTask,
        })}
      >
        {isCompleted ? (
          <button
            type="button"
            className={cn({ 'cursor-pointer': !disabled })}
            data-testid="StepStatusIcon"
            disabled={disabled}
            onClick={() => {
              setIsCompleted(prevState => !prevState);
              onCompleteToggle();
            }}
          >
            {!disabled ? (
              <IconCircleCheck className="text-feedback-success-base" />
            ) : (
              <IconCircleCheckDisabled className="text-grayscale-gray2" />
            )}
          </button>
        ) : (
          <button
            type="button"
            className={cn({ 'cursor-pointer': !disabled })}
            data-testid="StepStatusIcon"
            disabled={disabled}
            onClick={() => {
              setIsCompleted(prevState => !prevState);
              onCompleteToggle();
            }}
          >
            {!disabled ? (
              <IconCircle className="text-grayscale-gray2" />
            ) : (
              <IconCircleDisabled className="text-grayscale-gray2" />
            )}
          </button>
        )}

        <button
          type="button"
          className={cn('flex w-full flex-col text-left', {
            'cursor-pointer': canExpand && !disabled,
          })}
          data-testid={`PlanStep${step.id}`}
          disabled={(disabled ?? false) || !canExpand}
          onClick={() => onClick(step)}
        >
          <Typography
            data-testid="TaskTitle"
            variant="overline3"
            className="wrap-break-word whitespace-normal!"
          >
            {t('Generic.task')}
            {isCompleted &&
              userId &&
              ` - ${t('Generic.completedIn')} ${stepDuration} ${t(
                'Generic.mins',
              )}`}
          </Typography>
          <Typography
            data-testid="TaskOrProcedureName"
            variant="body1"
            className="wrap-break-word whitespace-normal!"
          >
            {itemByLanguage(step.name, undefined, language)}
          </Typography>
        </button>
        <button
          type="button"
          className={cn('text-grayscale-gray2 flex', {
            'cursor-pointer': canExpand && !disabled,
          })}
          disabled={(disabled ?? false) || !canExpand}
          onClick={() => onClick(step)}
        >
          {hasNotes && <IconNote />}
          {hasUrls && <IconLink />}
        </button>
      </div>
      {expandTask && (
        <>
          <div className="flex w-full flex-col pl-10">
            {hasNotes && (
              <div className="pb-2.5">
                <Typography variant="overline3">
                  {t('Generic.notes')}
                </Typography>
                <Typography variant="body1">{noteName}</Typography>
              </div>
            )}
            {hasUrls &&
              stepUrls?.map((link, index) => (
                <PlanItemStepLink
                  link={link}
                  language={language}
                  key={link.urlSet?.en ?? index}
                  planId={planId}
                  stepId={step.id}
                  stepName={itemByLanguage(step.name, undefined, language)}
                />
              ))}
          </div>
        </>
      )}
    </>
  );
};

export default PlanItemTask;
